import { useState } from 'react';
import { View } from 'react-native';
import { User, Appointment, TabType, Task } from './src/types';
import { Sidebar } from './src/components/Sidebar';
import { CalendarView, TasksView, PatientsView, MessagesView, PatientDetailView, RobotsView } from './src/screens';
import { styles } from './src/styles';
import { appointmentsData as initialAppointments, tasksData as initialTasks } from './src/constants';
import { useLocalStorage } from './src/hooks/useLocalStorage';
import { ThemeProvider, useTheme } from './src/theme';
import initialUsers from './data/users.json' with { type: 'json' };

export default function App() {
  return (
    <ThemeProvider>
      <AppInner />
    </ThemeProvider>
  );
}

function AppInner() {
  const { theme } = useTheme();
  const [appMode, setAppMode] = useLocalStorage<'pvs' | 'robot'>('pvs_app_mode', 'pvs');
  const [activeTab, setActiveTab] = useState<TabType>('calendar');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useLocalStorage<User[]>('pvs_users', initialUsers);
  const [appointments, setAppointments] = useLocalStorage<Appointment[]>('pvs_appointments', initialAppointments);
  const [tasks, setTasks] = useLocalStorage<Task[]>('pvs_tasks', initialTasks);

  const handlePatientSelect = (user: User) => {
    setSelectedUser(user);
  };

  const handleBack = () => {
    setSelectedUser(null);
  };

  const handleUserUpdate = (updatedUser: User) => {
    setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
    setSelectedUser(updatedUser);
  };

  const handleAddAppointment = (appointment: Appointment) => {
    setAppointments([...appointments, appointment]);
  };

  const handleUpdateAppointment = (updatedAppointment: Appointment) => {
    if ('title' in updatedAppointment && updatedAppointment.title === '__DELETED__') {
      setAppointments(appointments.filter(a => a.id !== updatedAppointment.id));
    } else {
      setAppointments(appointments.map(a => a.id === updatedAppointment.id ? updatedAppointment : a));
    }
  };

  const handleMoveAppointment = (appointmentId: number, newDate: Date) => {
    setAppointments(appointments.map(a => {
      if (a.id === appointmentId) {
        const [hours, minutes] = a.time.split(':');
        const newDateTime = new Date(newDate);
        newDateTime.setHours(parseInt(hours), parseInt(minutes));
        return {
          ...a,
          time: `${newDateTime.getHours().toString().padStart(2, '0')}:${newDateTime.getMinutes().toString().padStart(2, '0')}`
        };
      }
      return a;
    }));
  };

  const handleToggleTask = (taskId: number) => {
    setTasks(tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t));
  };

  const handleAddTask = (title: string) => {
    setTasks([...tasks, { id: Date.now(), title, completed: false, createdAt: new Date().toISOString() }]);
  };

  if (selectedUser) {
    return (
      <View style={[styles.appContainer, { backgroundColor: theme.appBg }]}>
        <Sidebar activeTab="patients" onTabChange={(tab) => { setActiveTab(tab); setSelectedUser(null); }} appMode={appMode} onAppModeChange={setAppMode} />
        <PatientDetailView
          user={selectedUser}
          onBack={handleBack}
          onUserUpdate={handleUserUpdate}
        />
      </View>
    );
  }

  return (
    <View style={[styles.appContainer, { backgroundColor: theme.appBg }]}>
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} appMode={appMode} onAppModeChange={setAppMode} />
      <View style={[styles.mainContent, { backgroundColor: theme.appBg }]}>
        {activeTab === 'calendar' && appMode === 'pvs' && (
          <CalendarView
            users={users}
            onPatientSelect={handlePatientSelect}
            appointments={appointments}
            onAddAppointment={handleAddAppointment}
            onMoveAppointment={handleMoveAppointment}
            onUpdateAppointment={handleUpdateAppointment}
          />
        )}
        {activeTab === 'calendar' && appMode === 'robot' && <RobotsView users={users} />}
        {activeTab === 'tasks' && (
          <TasksView 
            tasks={tasks}
            onToggleTask={handleToggleTask}
            onAddTask={handleAddTask}
          />
        )}
        {activeTab === 'patients' && (
          <PatientsView
            users={users}
            onSelectPatient={handlePatientSelect}
            title={appMode === 'robot' ? 'Techniker' : 'Patienten'}
            searchPlaceholder={appMode === 'robot' ? 'Techniker suchen...' : 'Patient suchen...'}
          />
        )}
        <View style={{ display: activeTab === 'messages' ? 'flex' : 'none', flex: 1 }}>
          <MessagesView />
        </View>
      </View>
    </View>
  );
}
